/* eslint-disable max-statements */

import { Box, Loader } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';

import { BatchListItem } from '@e-origin/contracts';
import { BatchStatusEnum } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { IconBox, SendingIndicator } from '@components';
import { extractBatchStatusByIcon } from './utils';

interface BatchLVStatusIndicatorProps {
  batch: BatchListItem;
}

const NO_STATISTICS_STATUSES = ['failed', 'in_progress', 'in_validation', 'scraping', 'sending', 'syncing'];

export const BatchLVStatusIndicator: React.FC<BatchLVStatusIndicatorProps> = (props) => {
  const { batch } = props;
  const batchName = batch.name;

  const hasCustomerProcessingIcon = batch.status.value === BatchStatusEnum.IN_VALIDATION;

  const hasRejectedIcon = !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics ? batch.statistics.rejected > 0 : false;

  const hasNotReleasedIcon = !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics ? batch.statistics?.notReleased > 0 : false;

  const initialHasNotSentIcon =
    !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics ? batch.statistics?.sent < batch.statistics?.total : false;

  const hasReleasedIcon =
    !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics ? batch.statistics?.released === batch.statistics?.total : false;

  const hasInvalidatedIcon =
    !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics ? batch.statistics?.invalidated === batch.statistics?.total : false;

  const hasCustomsProcessingIcon =
    !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics
      ? ((batch.statistics.sent === batch.statistics.total &&
          batch.statistics.released < batch.statistics.sent &&
          batch.statistics.invalidated < batch.statistics.sent) ||
          (batch.statistics.beGateSent === batch.statistics.total &&
            batch.statistics.beGateReleased < batch.statistics.beGateSent &&
            batch.statistics.beGateNotReleased < batch.statistics.beGateSent)) &&
        !batch.statistics.notReleased &&
        !batch.statistics.rejected
      : false;

  const hasSendingIcon = batch.status.value === BatchStatusEnum.SENDING && batch.statistics && batch.statistics?.sent < batch.statistics?.total;

  const anomaliesDetected = batch.statistics?.analysisSplitShipment > 0 || batch.statistics?.analysisIOSS > 0 || batch.statistics?.analysisUndervaluation > 0;

  const hasSyncingIcon = batch.status.value === 'syncing';

  const [hasFailedIcon, setHasFailedIcon] = useState(batch.status.value === 'failed');
  const [hasNotSentIcon, setHasNotSentIcon] = useState(initialHasNotSentIcon);
  const [hasProcessingIcon, setHasProcessingIcon] = useState(batch.status.value === 'in_progress');

  const previousBatchStatus = useRef<string | undefined>(undefined);

  const hasInvalidationPendingIcon =
    !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics ? batch.statistics?.invalidationPending > 0 : false;

  useEffect(() => {
    if (batch.status.value !== previousBatchStatus.current) {
      if (hasProcessingIcon && ['success', 'failed'].includes(batch.status.value)) {
        setHasProcessingIcon(false);

        if (batch.status.value === 'failed') {
          setHasFailedIcon(true);
        } else {
          setHasNotSentIcon(true);
        }
      }
      previousBatchStatus.current = batch.status.value;
    }
  }, [batch.status.value]);

  if (hasFailedIcon) {
    return (
      <Box display={'flex'}>
        <IconBox icon={IconFactory.failed} alt={'failed'} title={extractBatchStatusByIcon(IconFactory.failed)} />
      </Box>
    );
  }

  if (hasSendingIcon) {
    return (
      <Box display={'flex'}>
        <SendingIndicator data-pw={`batch-status-sending-${batchName}`} />
      </Box>
    );
  }

  if (hasCustomsProcessingIcon || hasInvalidationPendingIcon) {
    return (
      <Box display={'flex'}>
        <IconBox
          icon={IconFactory.processing}
          alt={'customs-processing'}
          title={extractBatchStatusByIcon(IconFactory.processing)}
          data-pw={`batch-status-processing-${batchName}`}
        />
      </Box>
    );
  }

  // show splitShipment status icons only if analysisSplitShipment is defined (i.e. older declarations
  // will not be takend into account) and if there are declarations in NOT_SENT status
  if (
    batch.statistics.sent !== batch.statistics.total &&
    (batch.statistics.analysisSplitShipment !== undefined ||
      batch.statistics.analysisIOSS !== undefined ||
      batch.statistics.analysisUndervaluation !== undefined)
  ) {
    return (
      <Box display={'flex'}>
        {anomaliesDetected && (
          <IconBox icon={IconFactory.anomaliesHSCodeOrDescription} data-pw={`batch-status-anomalies-hs-code-or-description-${batchName}`} />
        )}
        {!anomaliesDetected && <IconBox icon={IconFactory.noSentAnomalies} data-pw={`batch-status-no-anomalies-detected-${batchName}`} />}
        {hasRejectedIcon && (
          <IconBox
            icon={IconFactory.rejected}
            alt={'rejected'}
            title={extractBatchStatusByIcon(IconFactory.rejected)}
            data-pw={`batch-status-rejected-${batchName}`}
          />
        )}
      </Box>
    );
  }

  return (
    <Box display={'flex'}>
      {hasNotSentIcon && <IconBox icon={IconFactory.notSent} alt={'not-sent'} title={extractBatchStatusByIcon(IconFactory.notSent)} />}
      {hasReleasedIcon && <IconBox icon={IconFactory.released} alt={'released'} title={extractBatchStatusByIcon(IconFactory.released)} />}
      {hasInvalidatedIcon && <IconBox icon={IconFactory.invalidated} alt={'invalidated'} title={extractBatchStatusByIcon(IconFactory.invalidated)} />}
      {hasRejectedIcon && <IconBox icon={IconFactory.rejected} alt={'rejected'} title={extractBatchStatusByIcon(IconFactory.rejected)} />}
      {hasNotReleasedIcon && <IconBox icon={IconFactory.notReleased} alt={'not-released'} title={extractBatchStatusByIcon(IconFactory.notReleased)} />}
      {hasSyncingIcon && <IconBox icon={IconFactory.customsSync} alt={'customs-sync'} title={extractBatchStatusByIcon(IconFactory.customsSync)} />}
      {hasProcessingIcon && <Loader size={24} />}
      {hasCustomerProcessingIcon && (
        <IconBox icon={IconFactory.customsProcessing} alt={'customer-processing'} title={extractBatchStatusByIcon(IconFactory.customsProcessing)} />
      )}
      {!hasFailedIcon &&
        !hasCustomerProcessingIcon &&
        !hasNotSentIcon &&
        !hasReleasedIcon &&
        !hasInvalidatedIcon &&
        !hasRejectedIcon &&
        !hasNotReleasedIcon &&
        !hasCustomsProcessingIcon &&
        !hasSendingIcon &&
        !hasSyncingIcon &&
        !hasProcessingIcon && <IconBox icon={IconFactory.processing} alt={'customs-processing'} title={extractBatchStatusByIcon(IconFactory.processing)} />}
    </Box>
  );
};
